import React, { useState, useEffect } from 'react';
import { OrderOutlet, OutletScheduleConfig, OrderType } from '../../../types';
import { DEFAULT_OUTLETS } from '../../../data/outletsData';
import { useContent } from '../../../context/ContentContext';
import { resolveMediaUrl } from '../../../utils/api';
import {
  subscribeToOutletAvailabilityRealtime,
  normalizeOutletKey,
  resolveOutletOperatingStatus,
  DEFAULT_OUTLET_SCHEDULES,
} from '../../../utils/supabaseOutletStatus';
import { ArrowLeft, Coffee, Lock, Truck, Store } from 'lucide-react';
import { motion } from 'motion/react';

interface OutletSelectorProps {
  onSelectOutlet: (outlet: OrderOutlet) => void;
  onClose?: () => void;
  orderType?: OrderType;
  onChangeOrderType?: (type: OrderType) => void;
}

export const OutletSelector: React.FC<OutletSelectorProps> = ({ onSelectOutlet, onClose, orderType, onChangeOrderType }) => {
  const { data } = useContent();
  const [availabilityMap, setAvailabilityMap] = useState<Record<string, boolean>>({
    sudirman: true,
    kelakap_7: true,
    'letgo-mpp': true,
  });
  const [schedulesMap, setSchedulesMap] = useState<Record<string, OutletScheduleConfig>>(
    DEFAULT_OUTLET_SCHEDULES
  );
  const [selectedId, setSelectedId] = useState<string>('sudirman');
  const [currentOrderType, setCurrentOrderType] = useState<OrderType>(orderType || 'TAKE AWAY');

  useEffect(() => {
    if (orderType) {
      setCurrentOrderType(orderType);
    }
  }, [orderType]);

  const handleSelectOrderType = (type: OrderType) => {
    setCurrentOrderType(type);
    onChangeOrderType?.(type);
    if (type === 'DELIVERY') {
      setSelectedId('sudirman');
    }
  };

  // Subscribe to realtime outlet order availability & schedule changes
  useEffect(() => {
    const unsubscribe = subscribeToOutletAvailabilityRealtime((statusMap, schedMap) => {
      setAvailabilityMap(statusMap);
      if (schedMap) {
        setSchedulesMap(schedMap);
      }
    });
    return () => unsubscribe();
  }, []);

  // Filter outlets based on orderType:
  // - DELIVERY: only Sudirman (exact match id === 'sudirman')
  // - PICK UP (TAKE AWAY): Sudirman and Kelakap 7 (excluding letgo-mpp)
  const availableOutlets = DEFAULT_OUTLETS.filter((o) => {
    if (currentOrderType === 'DELIVERY') {
      return o.id === 'sudirman';
    }
    return o.id === 'sudirman' || o.id === 'kelakap_7';
  });

  // If switched to DELIVERY and selectedId is not sudirman, ensure sudirman is selected
  useEffect(() => {
    if (currentOrderType === 'DELIVERY' && selectedId !== 'sudirman') {
      setSelectedId('sudirman');
    }
  }, [currentOrderType, selectedId]);

  const outlets: OrderOutlet[] = availableOutlets.map((outlet) => {
    let dynamicImage = outlet.image;
    let dynamicAddress = outlet.address;
    let dynamicHours = outlet.hours;

    if (outlet.id === 'sudirman') {
      const b5 = data?.branches?.find((b) => b.id === 'chapter-5') || data?.branches?.[0];
      if (b5) {
        dynamicImage = b5.bgImage || outlet.image;
        dynamicAddress = b5.address || outlet.address;
        dynamicHours = b5.openingHours || outlet.hours;
      }
    } else if (outlet.id === 'kelakap_7') {
      const b6 = data?.branches?.find((b) => b.id === 'chapter-6') || data?.branches?.[1];
      if (b6) {
        dynamicImage = b6.bgImage || outlet.image;
        dynamicAddress = b6.address || outlet.address;
        dynamicHours = b6.openingHours || outlet.hours;
      }
    }

    const normKey = normalizeOutletKey(outlet.id);
    const opStatus = resolveOutletOperatingStatus(normKey, schedulesMap, availabilityMap);

    return {
      ...outlet,
      image: dynamicImage,
      address: dynamicAddress,
      hours: dynamicHours,
      whatsapp: data?.contactSettings?.whatsapp || outlet.whatsapp,
      accepting_orders: opStatus.isOpen,
      scheduleConfig: schedulesMap[normKey],
      operatingStatus: opStatus,
    };
  });

  // Keep selectedId pointing to an accepting outlet if current selection is closed
  useEffect(() => {
    const current = outlets.find((o) => o.id === selectedId);
    if (!current || current.accepting_orders === false) {
      const firstOpen = outlets.find((o) => o.accepting_orders !== false);
      if (firstOpen && firstOpen.id !== selectedId) {
        setSelectedId(firstOpen.id);
      }
    }
  }, [outlets, selectedId]);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  const filteredOutlets = outlets.filter((outlet) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      outlet.name.toLowerCase().includes(q) ||
      (outlet.address || '').toLowerCase().includes(q)
    );
  });

  const UX_PILOT_OUTLET_FALLBACKS = [
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/gen_f052c7f7c9_bd37059ec203ab1b.png',
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/gen_612661bf83_6f4074d6dfcaed91.png',
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/gen_f2f4183569_e68b96f2d80ca9c5.png',
  ];

  return (
    <div className="min-h-screen w-full bg-[#F8FAFC] text-[#0A192F] pb-24 max-w-lg md:max-w-3xl lg:max-w-5xl mx-auto">
      {/* Header — Exact UX Pilot Select Outlet Structure */}
      <header 
        className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E2E8F0] px-4 lg:px-8"
        style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))', paddingBottom: '1rem' }}
      >
        <div className="flex items-center gap-3">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Kembali"
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0A192F] cursor-pointer shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <h1 className="text-xl font-bold text-[#0A192F]">Select Outlet</h1>
        </div>

        {/* Order Type Toggle */}
        <div className="mt-3 bg-[#F8FAFC] p-1.5 rounded-xl border border-[#E2E8F0] flex gap-1.5">
          <button
            type="button"
            onClick={() => handleSelectOrderType('TAKE AWAY')}
            className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 cursor-pointer transition-all ${
              currentOrderType === 'TAKE AWAY'
                ? 'bg-[#0A192F] text-white shadow-xs'
                : 'text-[#64748B] hover:text-[#0A192F] hover:bg-slate-100'
            }`}
          >
            <Store className="w-4 h-4" />
            <span>Self Pick-Up</span>
          </button>
          <button
            type="button"
            onClick={() => handleSelectOrderType('DELIVERY')}
            className={`flex-1 py-2 text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 cursor-pointer transition-all ${
              currentOrderType === 'DELIVERY'
                ? 'bg-[#0A192F] text-white shadow-xs'
                : 'text-[#64748B] hover:text-[#0A192F] hover:bg-slate-100'
            }`}
          >
            <Truck className="w-4 h-4" />
            <span>Delivery</span>
          </button>
        </div>

        {/* Search Input */}
        <div className="mt-3 relative">
          <svg
            className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search outlet location..."
            className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl py-2.5 pl-10 pr-4 text-base sm:text-sm text-[#0A192F] placeholder:text-slate-400 focus:outline-none focus:border-[#0056D2] transition-colors"
          />
        </div>

        {/* Informative indicator when in Delivery mode */}
        {currentOrderType === 'DELIVERY' && (
          <div className="mt-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl flex items-center gap-2 text-xs text-[#0056D2]">
            <Truck className="w-4 h-4 shrink-0" />
            <span className="font-semibold">Mode Delivery: Hanya tersedia di Outlet Sudirman</span>
          </div>
        )}
      </header>

      <main>
        {/* Map View Toggle */}
        <div className="px-4 lg:px-8 mt-4">
          <div className="bg-[#F8FAFC] p-1 rounded-xl border border-[#E2E8F0] flex">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`flex-1 py-2 text-xs rounded-lg transition-all cursor-pointer ${
                viewMode === 'list'
                  ? 'font-bold bg-white text-[#0A192F] shadow-sm'
                  : 'font-semibold text-slate-500'
              }`}
            >
              List View
            </button>
            <button
              type="button"
              onClick={() => setViewMode('map')}
              className={`flex-1 py-2 text-xs rounded-lg transition-all cursor-pointer ${
                viewMode === 'map'
                  ? 'font-bold bg-white text-[#0A192F] shadow-sm'
                  : 'font-semibold text-slate-500'
              }`}
            >
              Map View
            </button>
          </div>
        </div>

        {/* Outlet List */}
        <section className="px-4 lg:px-8 mt-4 space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-5" id="outlet-list">
          {filteredOutlets.map((outlet, idx) => {
            const isAccepting = outlet.accepting_orders !== false;
            const isSelected = outlet.id === selectedId;
            const opStatus = outlet.operatingStatus;
            const fallbackImg =
              UX_PILOT_OUTLET_FALLBACKS[idx % UX_PILOT_OUTLET_FALLBACKS.length];
            const resolvedImg = resolveMediaUrl(outlet.image) || fallbackImg;
            const distanceLabel = idx === 0 ? '0.8 km' : '2.4 km';

            return (
              <div
                key={outlet.id}
                onClick={() => {
                  if (isAccepting) {
                    setSelectedId(outlet.id);
                    onSelectOutlet(outlet);
                  }
                }}
                className={`bg-white rounded-2xl overflow-hidden relative transition-all ${
                  isSelected
                    ? 'border-2 border-[#0056D2] shadow-md'
                    : 'border border-[#E2E8F0] shadow-sm'
                } ${isAccepting ? 'cursor-pointer' : 'opacity-75 cursor-not-allowed'}`}
                data-outlet-id={outlet.id}
                data-outlet-name={outlet.name}
              >
                {isSelected && (
                  <div className="bg-[#0056D2] text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl absolute top-0 right-0 z-10">
                    SELECTED
                  </div>
                )}

                <div className="h-32 bg-slate-100 overflow-hidden">
                  <img
                    className={`w-full h-full object-cover ${
                      !isAccepting ? 'grayscale brightness-75' : ''
                    }`}
                    src={resolvedImg}
                    alt={outlet.name}
                    onError={(e) => {
                      const target = e.currentTarget;
                      if (target.src !== fallbackImg) {
                        target.src = fallbackImg;
                      }
                    }}
                  />
                </div>

                <div className="p-4">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-bold text-base text-[#0A192F]">{outlet.name}</h3>
                    <span
                      className={`text-xs font-bold ${
                        isSelected ? 'text-[#0056D2]' : 'text-slate-400 font-semibold'
                      }`}
                    >
                      {distanceLabel}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-1.5 mb-3">
                    <p className="text-xs text-slate-500 flex-1 min-w-[150px]">{outlet.address}</p>
                    {outlet.supportsDelivery !== false ? (
                      <span className="text-[10px] font-bold text-[#0056D2] bg-blue-50 px-2 py-0.5 rounded-md flex items-center gap-1 shrink-0">
                        <Truck className="w-3 h-3" />
                        <span>Delivery & Pick Up</span>
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md flex items-center gap-1 shrink-0">
                        <Store className="w-3 h-3" />
                        <span>Hanya Pick Up</span>
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-[#E2E8F0]">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          isAccepting ? 'bg-green-500' : 'bg-red-500'
                        }`}
                      />
                      <span className="text-[11px] font-semibold text-slate-600">
                        {isAccepting
                          ? `Open • ${outlet.hours || 'Closes 22:00'}`
                          : opStatus?.nextOpenTime || 'Closed'}
                      </span>
                    </div>

                    {isAccepting ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(outlet.id);
                          onSelectOutlet(outlet);
                        }}
                        className={`text-xs font-bold px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                          isSelected
                            ? 'bg-[#0056D2] text-white'
                            : 'bg-[#F8FAFC] text-[#0A192F] border border-[#E2E8F0] hover:bg-[#0056D2] hover:text-white hover:border-[#0056D2]'
                        }`}
                      >
                        Select Outlet
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled
                        className="bg-slate-100 text-slate-400 text-xs font-bold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 cursor-not-allowed"
                      >
                        <Lock className="w-3 h-3" />
                        <span>Closed</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </section>
      </main>
    </div>
  );
};



